import { useLocation } from 'react-router-dom';
import Header from './Header';
import AuthProvider from "./AuthProvider";
import AccountStatusBanner from "./AccountStatusBanner";
import { useState, useEffect, useContext } from 'react';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import NotificationModal from '../Common/NotificationModal';
import { markNotificationAsViewed } from '../../lib/api';
import { isCustomerPaidUpToDate, customerNeedsLicenseAgreement } from '../../utils/licenseAccess';

const AppShell = ({ children, shouldHideHeader, hideNav, isFleetPage }) => {
  const { currentUser, notification, dismissNotification } = useContext(CurrentUserContext);

  const handleNotificationClose = async () => {
    const notificationUnid = notification?.unid;

    dismissNotification();

    if (!notificationUnid) {
      return;
    }

    try {
      await markNotificationAsViewed(notificationUnid);
    } catch (error) {
      console.error("Failed to mark notification as viewed:", error);
    }
  };

  const shouldShowNotification =
    !hideNav &&
    Boolean(currentUser) &&
    Boolean(notification?.markdownContent) &&
    isCustomerPaidUpToDate(currentUser) &&
    !customerNeedsLicenseAgreement(currentUser);

  return (
    <>
      {!shouldHideHeader && <Header hideNav={hideNav} />}
      <main className={`grow flex flex-col justify-start  ${!shouldHideHeader && "bg-ivory dark:bg-darkThemeBg px-6 py-5"} ${isFleetPage ? 'min-h-0 overflow-auto pb-toolbar lg:pb-5' : ''}`}>
        {children}
      </main>
      {shouldShowNotification && (
        <NotificationModal
          markdownContent={notification.markdownContent}
          onClose={handleNotificationClose}
        />
      )}
    </>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const noHeaderRoutes = ['/login', '/forgotpassword', '/landingpage'];
  const noNavRoutes = ['/api-docs'];

  const shouldHideHeader = noHeaderRoutes.includes(location.pathname);
  const hideNav = noNavRoutes.includes(location.pathname);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkForModal = () => {
      const modalPresent = !!document.querySelector('.overlayHolder');
      setIsModalOpen(modalPresent);
    };

    checkForModal(); // Run on mount

    const observer = new MutationObserver(() => {
      checkForModal();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // overflow:hidden on body still lets wheel/touch input scroll the page in some
  // browsers via scroll chaining, so pin body with position:fixed instead, which
  // takes it out of any scrollable box entirely.
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const scrollY = window.scrollY;
    const { style } = document.body;
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.left = '0';
    style.right = '0';

    return () => {
      style.position = '';
      style.top = '';
      style.left = '';
      style.right = '';
      window.scrollTo(0, scrollY);
    };
  }, [isModalOpen]);

  const isFleetPage = location.pathname === '/units' || location.pathname === '/alerts' || location.pathname === '/dashboard' || location.pathname === '/control' || location.pathname === '/energy' ;


  return (
    <div
      className={`appWrapper flex flex-col min-h-screen ${isModalOpen ? 'overflow-hidden' : ''} ${isFleetPage ? 'h-screen overflow-hidden lg:h-auto lg:overflow-visible' : ''}`}
      style={isModalOpen ? { overflow: 'hidden' } : undefined}
    >
      <AuthProvider skipLoadingGate={hideNav}>
        <AccountStatusBanner hidden={hideNav}>
          <AppShell shouldHideHeader={shouldHideHeader} hideNav={hideNav} isFleetPage={isFleetPage}>
            {children}
          </AppShell>
        </AccountStatusBanner>
      </AuthProvider>
    </div>
  );
};

export default Layout;
